import type { ComponentType } from "react";
import type { CmmdBoardTask, CmmdProjectMilestone } from "@cmmd-forge/contracts";
import {
  ArchiveIcon,
  CalendarRangeIcon,
  CircleDotIcon,
  KanbanIcon,
  LayoutListIcon,
  PlusIcon,
  UserIcon,
  FlagIcon,
} from "lucide-react";

import {
  PlanningDisplayPopover,
  PlanningDisplaySection,
} from "~/components/data-views/PlanningDisplayPopover";
import { PlanningExpandingSearch } from "~/components/data-views/PlanningExpandingSearch";
import { planningIconControlClass } from "~/components/data-views/planningIconControl";
import { PlanningViewSwitcher } from "~/components/data-views/PlanningViewChrome";
import {
  ViewsAndFiltersPanel,
  type ViewsFilterCategory,
} from "~/components/data-views/ViewsAndFiltersPanel";
import { cn } from "~/lib/utils";
import type { DataViewController } from "~/views/viewController";
import {
  projectTaskSurfaceView,
  setProjectTaskSurfaceView,
  type ProjectTaskSurfaceView,
} from "./projectTaskBacklogView";
import {
  buildTaskAssigneeFilterItems,
  buildTaskMilestoneFilterItems,
  buildTaskStatusFilterItems,
} from "./taskViewFilters";

type TaskView = ProjectTaskSurfaceView;

const VIEW_OPTIONS: ReadonlyArray<{
  value: TaskView;
  label: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  { value: "backlog", label: "Backlog", icon: ArchiveIcon },
  { value: "list", label: "List", icon: LayoutListIcon },
  { value: "board", label: "Board", icon: KanbanIcon },
  { value: "timeline", label: "Timeline", icon: CalendarRangeIcon },
];

export interface ProjectTaskTrailingControlsProps {
  readonly viewController: DataViewController;
  readonly composerOpen: boolean;
  readonly onToggleComposer: () => void;
  readonly savedViewsEnabled?: boolean;
  /** Root board tasks — drives Status/Assignee filter option counts. */
  readonly tasks?: ReadonlyArray<CmmdBoardTask>;
  readonly milestones?: ReadonlyArray<CmmdProjectMilestone>;
  readonly onManageMilestones?: (() => void) | undefined;
  readonly taskMutationsDisabled?: boolean;
}

/**
 * Linear-style tasks chrome: expanding search, Display popover, New task.
 * Built on shared Planning* chrome primitives.
 */
export function ProjectTaskTrailingControls(props: ProjectTaskTrailingControlsProps) {
  const savedViewsEnabled = props.savedViewsEnabled !== false;
  const search = props.viewController.state.search;
  const displayActive =
    props.viewController.state.layout !== "list" ||
    props.viewController.state.statusFilter.length > 0 ||
    props.viewController.state.ownerFilter.length > 0 ||
    props.viewController.state.milestoneFilter.length > 0 ||
    props.viewController.state.taskGroupBy !== "status" ||
    props.viewController.activeSavedView != null ||
    props.viewController.isDirty;

  return (
    <div className="hidden min-w-0 items-center gap-1.5 md:flex">
      <PlanningExpandingSearch
        search={search}
        onSearchChange={(next) => props.viewController.patchState({ search: next })}
        ariaLabel="Search tasks"
        placeholder="Filter by title…"
      />
      <TaskDisplayPopover
        viewController={props.viewController}
        savedViewsEnabled={savedViewsEnabled}
        active={displayActive}
        tasks={props.tasks ?? []}
        milestones={props.milestones ?? []}
      />
      {props.onManageMilestones ? (
        <button
          type="button"
          aria-label="Manage milestones"
          title="Manage milestones"
          onClick={props.onManageMilestones}
          className={planningIconControlClass({})}
        >
          <FlagIcon className="size-3.5" aria-hidden />
        </button>
      ) : null}
      <button
        type="button"
        aria-label="New task"
        title="New task"
        aria-pressed={props.composerOpen}
        onClick={props.onToggleComposer}
        disabled={props.taskMutationsDisabled}
        className={cn(
          planningIconControlClass({
            active: props.composerOpen,
            primary: !props.composerOpen,
          }),
        )}
      >
        <PlusIcon className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

export function TaskDisplayPopover(props: {
  readonly viewController: DataViewController;
  readonly savedViewsEnabled: boolean;
  readonly active: boolean;
  readonly tasks: ReadonlyArray<CmmdBoardTask>;
  readonly milestones: ReadonlyArray<CmmdProjectMilestone>;
  readonly triggerClassName?: string;
}) {
  const layout = props.viewController.state.layout;
  const surfaceView = projectTaskSurfaceView(props.viewController.state);
  const statusFilter = props.viewController.state.statusFilter;
  const ownerFilter = props.viewController.state.ownerFilter;
  const milestoneFilter = props.viewController.state.milestoneFilter;
  const status = buildTaskStatusFilterItems(props.tasks);
  const assigneeItems = buildTaskAssigneeFilterItems(props.tasks);
  const milestoneItems = buildTaskMilestoneFilterItems(props.tasks, props.milestones);

  const categories: ReadonlyArray<ViewsFilterCategory> = [
    {
      id: "status",
      label: "Status",
      icon: CircleDotIcon,
      value: statusFilter,
      items: status.items,
      onChange: (value) => props.viewController.patchState({ statusFilter: value }),
    },
    {
      id: "milestone",
      label: "Milestone",
      icon: FlagIcon,
      value: milestoneFilter,
      items: milestoneItems,
      onChange: (value) => props.viewController.patchState({ milestoneFilter: value }),
    },
    {
      id: "assignee",
      label: "Assignee",
      icon: UserIcon,
      value: ownerFilter,
      items: assigneeItems,
      onChange: (value) => props.viewController.patchState({ ownerFilter: value }),
    },
  ];

  return (
    <PlanningDisplayPopover
      active={props.active}
      {...(props.triggerClassName ? { className: props.triggerClassName } : {})}
    >
      <PlanningDisplaySection sectionKey="layout">
        <PlanningViewSwitcher
          value={surfaceView}
          options={VIEW_OPTIONS}
          onChange={(next) => setProjectTaskSurfaceView(props.viewController, next)}
          showLabels
          fill
        />
      </PlanningDisplaySection>
      {layout === "timeline" ? null : (
        <PlanningDisplaySection title="Group by">
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            {(["status", "milestone"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={props.viewController.state.taskGroupBy === value}
                onClick={() => props.viewController.patchState({ taskGroupBy: value })}
                className={cn(
                  "min-h-touch rounded-lg px-3 text-sm font-medium capitalize transition-colors md:min-h-9 md:rounded-md md:text-xs",
                  props.viewController.state.taskGroupBy === value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {value}
              </button>
            ))}
          </div>
        </PlanningDisplaySection>
      )}
      <ViewsAndFiltersPanel
        controller={props.viewController}
        categories={categories}
        showSavedViews={props.savedViewsEnabled}
      />
    </PlanningDisplayPopover>
  );
}
